import React, { useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Helmet } from "react-helmet-async"
import {
  clearMatches,
  fetchSchedules,
  selectSchedulesByContext,
  selectSchedulesByIds,
} from "./slice"
import NavMain from "../../app/NavMain"
import DataPageHeader from "../../ui/DataPageHeader"
import ScrollablePage from "../../ui/ScrollablePage"
import Paper from "../../ui/Paper"
import Layout from "../../ui/Layout"
import { useLocation, useNavigate } from "react-router-dom"
import { updateSearchParamsWithFilters } from "../filters/filters"
import { parseScheduleFilters } from "./filters"
import SchedulesTable from "./SchedulesTable"
import ErrorMsg from "../../ui/ErrorMsg"
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner"
import NameIDHeader from "../../ui/NameIDHeader"

const PAGE_SIZE = 50 // Default initial and subsequent page size

export default function SchedulesList() {
  const dispatch = useDispatch()

  // we get any matching schedules
  const scheduleMatches = useSelector((state) =>
    selectSchedulesByContext(state, "root")
  )
  // We get the full objects from the ids that match
  const schedules = useSelector((state) =>
    selectSchedulesByIds(state, scheduleMatches?.ids || [])
  )

  // Parse parameters into filter object (which drives filters UI state)
  // Note: We are using the URL as the "state" here, and updates
  // perform a history replacement (so as not to mess up "back" button
  // behaviour too much - though we may want to apply a heuristic like
  // small changes replace history, big changes push history)
  const location = useLocation()
  const navigate = useNavigate()
  const urlParams = new URLSearchParams(location.search)
  const filter = parseScheduleFilters(urlParams)

  updateSearchParamsWithFilters(urlParams, filter)

  useEffect(() => {
    dispatch(clearMatches)
    dispatch(
      fetchSchedules({
        filter,
        page: { offset: 0, limit: PAGE_SIZE },
        context: ["root"],
      })
    )
  }, [urlParams.toString()])

  // How we apply a filter update. Filter state is entirely in the URL query parameters
  const setFilter = (filter = {}) => {
    const updUrlParams = updateSearchParamsWithFilters(urlParams, filter)
    navigate({
      pathname: location.pathname,
      search: `?${updUrlParams}`,
    })
  }

  // How many more schedules are available to load
  // How many more observations are available to load?
  const loadableCount = Math.min(
    PAGE_SIZE,
    schedules &&
      schedules.length &&
      scheduleMatches &&
      scheduleMatches.page &&
      scheduleMatches.page.total_count
      ? scheduleMatches.page.total_count - schedules.length
      : 50
  )

  // Event handler for 'Show xxx more' button that was clicked
  const handleLoadMore = () => {
    const lastIndex = schedules && schedules.length

    const page = {
      offset: lastIndex,
      limit: loadableCount,
    }
    dispatch(fetchSchedules({ filter, page, context: ["root"] }))
  }

  return (
    <Layout>
      <Helmet>
        <title>Schedules - Astrosite</title>
      </Helmet>
      <NavMain />
      {schedules.length === 0 && scheduleMatches?._loadStatus === "loading" && (
        <Backdrop>
          <LoadingSpinner />
        </Backdrop>
      )}
      <ScrollablePage>
        <DataPageHeader
          title={<NameIDHeader name={"Schedules"} />}
          subtitle={
            <>
              {schedules.length > 0 && (
                <span>
                  Showing <strong>{scheduleMatches?.page?.end_index}</strong>
                  {scheduleMatches &&
                    scheduleMatches.page &&
                    scheduleMatches.page.total_count && (
                      <span>
                        {" "}
                        of{" "}
                        <strong className="count total_count">
                          {scheduleMatches.page.total_count}
                        </strong>
                      </span>
                    )}{" "}
                  Schedule&apos;s
                  {scheduleMatches?._loadStatus === "loading" && (
                    <span> updating...</span>
                  )}
                </span>
              )}
              {schedules.length === 0 &&
                scheduleMatches?._loadStatus === "loading" && (
                  <span>loading...</span>
                )}
              {scheduleMatches?._loadStatus === "rejected" && (
                <ErrorMsg
                  msg={"There is a problem fetching the schedules"}
                  showIcon={true}
                />
              )}
            </>
          }
        />
        <Paper>
          <SchedulesTable
            schedules={schedules}
            filter={filter}
            setFilter={setFilter}
          />
          <footer className="actions">
            {scheduleMatches &&
              scheduleMatches.page &&
              schedules.length > 0 &&
              schedules.length !== scheduleMatches.page.total_count && (
                <>
                  <button
                    onClick={handleLoadMore}
                    disabled={scheduleMatches?._loadStatus === "loading"}
                  >
                    {scheduleMatches?._loadStatus === "loading"
                      ? "Loading..."
                      : `Show ${loadableCount} more`}
                  </button>
                </>
              )}
          </footer>
        </Paper>
      </ScrollablePage>
    </Layout>
  )
}
